import { LitheElement, LitheFC } from '@wheelbox/lithejs-engine';

export interface TerminalCommand {
  text: string;
  delay?: number;
  output?: string;
}

export interface AnimatedTerminalProps {
  id?: string;
  testId?: string;
  commands: TerminalCommand[];
  class?: string;
  autoStart?: boolean;
  loop?: boolean;
  typingSpeed?: number;
}

const AnimatedTerminal: LitheFC<AnimatedTerminalProps> = ({
  id = 'animated-terminal',
  testId = 'animated-terminal',
  commands,
  class: className = '',
  autoStart = true,
  loop = true,
  typingSpeed = 50,
}): LitheElement<'div'> => {
  const outputId = `${id}-output`;
  let currentTimeout: ReturnType<typeof setTimeout> | null = null;
  let isRunning = false;

  const typeText = async (
    element: HTMLElement,
    text: string,
    speed: number
  ): Promise<void> => {
    return new Promise((resolve) => {
      let index = 0;
      const type = () => {
        if (index < text.length) {
          element.textContent += text[index];
          index++;
          currentTimeout = setTimeout(type, speed);
        } else {
          resolve();
        }
      };
      type();
    });
  };

  const runAnimation = async (outputElement?: HTMLElement) => {
    const output =
      outputElement || (document.getElementById(outputId) as HTMLElement);
    if (!output || isRunning) return;

    isRunning = true;

    const runCommands = async () => {
      if (!isRunning) return;
      output.innerHTML = '';

      for (const command of commands) {
        if (!isRunning) return;

        // Create prompt line
        const promptLine = document.createElement('div');
        promptLine.className = 'flex items-center gap-2 font-mono text-sm';

        const prompt = document.createElement('span');
        prompt.className = 'text-green-400';
        prompt.textContent = '❯';
        promptLine.appendChild(prompt);

        const commandText = document.createElement('span');
        commandText.className = 'text-gray-200';
        promptLine.appendChild(commandText);

        output.appendChild(promptLine);

        // Type command
        await typeText(commandText, command.text, typingSpeed);

        // Wait before next command
        await new Promise((resolve) => {
          currentTimeout = setTimeout(resolve, command.delay || 500);
        });

        // Show output if present
        if (command.output) {
          const outputLine = document.createElement('div');
          outputLine.className = 'text-gray-400 text-sm font-mono pl-5';
          outputLine.textContent = command.output;
          output.appendChild(outputLine);
        }
      }

      // Loop animation
      if (loop && isRunning) {
        await new Promise((resolve) => {
          currentTimeout = setTimeout(resolve, 3000);
        });
        if (isRunning) runCommands();
      }
    };

    if (autoStart) {
      runCommands();
    }
  };

  const onMountHandler = (element: Element) => {
    // Use requestAnimationFrame to ensure DOM is ready
    requestAnimationFrame(() => {
      const outputElement = element.querySelector(
        `#${outputId}`
      ) as HTMLElement;
      if (outputElement) {
        runAnimation(outputElement);
      }
    });
  };

  const onUnmountHandler = () => {
    isRunning = false;
    if (currentTimeout) {
      clearTimeout(currentTimeout);
    }
  };

  return [
    'div',
    {
      id,
      'data-testid': testId,
      class: `rounded-lg overflow-hidden shadow-2xl ${className}`,
      onmount: onMountHandler,
      onunmount: onUnmountHandler,
    },
    // Terminal header
    [
      'div',
      { class: 'bg-gray-800 px-4 py-3 flex items-center gap-2' },
      ['div', { class: 'w-3 h-3 rounded-full bg-red-500' }],
      ['div', { class: 'w-3 h-3 rounded-full bg-yellow-500' }],
      ['div', { class: 'w-3 h-3 rounded-full bg-green-500' }],
      ['span', { class: 'ml-4 text-gray-400 text-sm font-mono' }, 'terminal'],
    ],
    // Terminal body
    [
      'div',
      {
        id: outputId,
        class: 'bg-gray-900 p-4 min-h-[120px] space-y-2',
      },
    ],
  ];
};

export default AnimatedTerminal;
